import React, { useMemo, useRef, useState } from "react";

import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import WeeklyEACHeader from "./weekly-eac/WeeklyEACHeader";
import WeeklyEACMeta from "./weekly-eac/WeeklyEACMeta";
import WeeklyEACDirectLaborTable from "./weekly-eac/WeeklyEACDirectLaborTable";
import WeeklyEACODCTable from "./weekly-eac/WeeklyEACODCTable";
import WeeklyEACSummaryTable from "./weekly-eac/WeeklyEACSummaryTable";
import { computeWeeklyTotals } from "./weekly-eac/weeklyEACWeeklyTotals";
import { useWeeklyEACWeekNavigation } from "./weekly-eac/useWeeklyEACWeekNavigation";
import { useWeeklyEACEditableData } from "./weekly-eac/useWeeklyEACEditableData";

export default function WeeklyEACs({
  contractId,
  clin,
  version,
  type,
  isEditable = true,
  directLaborData,
  loading,
  onSaved,
  detailedViewRatesData,
  handleClickViewRates,
}) {
  const [searchLcat, setSearchLcat] = useState("");

  const {
    clinDetails,
    originalWeeklyTotals,
    employeeDetails,
    odcDetails,
    cocrDetails,
    isEdit,
    setIsEdit,
    isSaving,
    msgType,
    msgText,
    showMessage,
    isMessageClosed,
    handleCocrValueChange,
    handleHoursChange,
    handleUnburdenCostChange,
    handleSave,
    handleCancel,
  } = useWeeklyEACEditableData({
    contractId,
    clin,
    version,
    directLaborData,
    detailedViewRatesData,
    onSaved,
  });

  const directLaborScrollRef = useRef(null);
  const odcScrollRef = useRef(null);
  const summaryScrollRef = useRef(null);
  const scrollRefsBySource = {
    directLabor: directLaborScrollRef,
    odc: odcScrollRef,
    summary: summaryScrollRef,
  };

  // All three scrollable tables (Direct Labor, Other Direct Costs, Summary) move together.
  const handleScroll = (source) => {
    const origin = scrollRefsBySource[source].current;
    if (!origin) return;
    Object.entries(scrollRefsBySource).forEach(([key, ref]) => {
      if (key !== source && ref.current) ref.current.scrollLeft = origin.scrollLeft;
    });
  };

  const filteredEmployeeDetails = useMemo(() => {
    if (!searchLcat) return employeeDetails;
    const term = searchLcat.toLowerCase();
    return employeeDetails.filter((employee) => {
      const idMatch = employee.lcatId?.toLowerCase().includes(term);
      const descriptionMatch = employee.lcatDescription?.toLowerCase().includes(term);
      return idMatch || descriptionMatch;
    });
  }, [employeeDetails, searchLcat]);

  const computedWeeklyTotals = useMemo(
    () =>
      computeWeeklyTotals(
        employeeDetails,
        clinDetails,
        originalWeeklyTotals,
        odcDetails,
        cocrDetails,
        type,
      ),
    [employeeDetails, clinDetails, originalWeeklyTotals, odcDetails, cocrDetails, type],
  );

  const { selectedWeekStartDate, weekOptions, handleWeekSelect } = useWeeklyEACWeekNavigation({
    directLaborData,
    computedWeeklyTotals,
    type,
    scrollRefs: [directLaborScrollRef, odcScrollRef, summaryScrollRef],
  });

  if (loading) {
    return <Loading />;
  }

  if (!directLaborData) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        No Weekly EAC data available for this CLIN.
      </div>
    );
  }

  return (
    <div>
      {showMessage && (
        <ActionMessages type={msgType} isClosed={isMessageClosed} message={msgText} />
      )}
      <WeeklyEACHeader
        detailedViewRatesData={detailedViewRatesData}
        handleClickViewRates={handleClickViewRates}
        searchLcat={searchLcat}
        setSearchLcat={setSearchLcat}
      />
      <WeeklyEACMeta clinDetails={clinDetails} />
      <WeeklyEACDirectLaborTable
        employeeDetails={filteredEmployeeDetails}
        allEmployeeDetails={employeeDetails}
        hasOdcData={odcDetails.length > 0}
        type={type}
        isEdit={isEdit}
        isEditable={isEditable}
        isSaving={isSaving}
        onEdit={() => setIsEdit(true)}
        onSave={handleSave}
        onCancel={handleCancel}
        onHoursChange={handleHoursChange}
        scrollRef={directLaborScrollRef}
        onScroll={() => handleScroll("directLabor")}
        weekOptions={weekOptions}
        selectedWeekStartDate={selectedWeekStartDate}
        onWeekSelect={handleWeekSelect}
      />
      <WeeklyEACODCTable
        odcDetails={odcDetails}
        allOdcDetails={odcDetails}
        type={type}
        isEdit={isEdit}
        onUnburdenCostChange={handleUnburdenCostChange}
        scrollRef={odcScrollRef}
        onScroll={() => handleScroll("odc")}
      />
      <WeeklyEACSummaryTable
        weeklyTotals={computedWeeklyTotals}
        type={type}
        isEdit={isEdit}
        onCreditOverCeilingChange={handleCocrValueChange}
        scrollRef={summaryScrollRef}
        onScroll={() => handleScroll("summary")}
      />
    </div>
  );
}
